import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {FundInvestment} from "../models/fund-investement";
import {
    FundApiService,
    UpdateFundInvestmentRealizationRequest,
    UpdateFundInvestmentRecentFinancialPerformanceRequest,
    UpdateFundInvestmentDevelopmentsSinceInvestmentRequest,
    UpdateFundInvestmentTransactionSummaryRequest
} from "../generated-sources/api";
import {PartnershipInvestment} from "../models/partnership-investment";

@Injectable({
    providedIn: "root"
})
export class FundInvestmentService {
    private readonly fundApiService = inject(FundApiService)

    constructor() {
    }

    public getInvestmentsForFundReport(fundId: number, fundReportId: number): Observable<FundInvestment[]> {
        return this.fundApiService.getInvestmentsForFundReport(fundId, fundReportId);
    }

    public updateFundInvestments(fundId: number, fundReportId: number, investments: FundInvestment[], fundValuationId?: number): Observable<FundInvestment[]> {
        return this.fundApiService.updateFundInvestments(fundId, fundReportId, investments, fundValuationId);
    }

    public getFundInvestment(fundId: number, fundReportId: number, investmentId: number): Observable<FundInvestment> {
        return this.fundApiService.getFundInvestment(fundId, fundReportId, investmentId);
    }

    public updateFundInvestment(fundId: number, fundReportId: number, investmentId: number, investment: FundInvestment, fundValuationId?: number): Observable<FundInvestment> {
        return this.fundApiService.updateFundInvestment(fundId, fundReportId, investmentId, investment, fundValuationId);
    }

    public updateFundInvestmentRealization(
        fundId: number,
        fundReportId: number,
        investmentId: number,
        isRealized: boolean,
        investmentDate?: string,
        exitDate?: string,
        fundValuationId?: number
    ): Observable<FundInvestment> {
        const updateFundInvestmentRealizationRequest: UpdateFundInvestmentRealizationRequest = {
            investmentDate,
            exitDate,
            isRealized
        }
        return this.fundApiService.updateFundInvestmentRealization(fundId, fundReportId, investmentId, updateFundInvestmentRealizationRequest, fundValuationId)
    }

    public updateFundInvestmentTransactionSummary(
        fundId: number,
        fundReportId: number,
        investmentId: number,
        transactionSummary: string
    ): Observable<FundInvestment> {
        const updateFundInvestmentTransactionSummaryRequest: UpdateFundInvestmentTransactionSummaryRequest = {
            transactionSummary
        }
        return this.fundApiService.updateFundInvestmentTransactionSummary(fundId, fundReportId, investmentId, updateFundInvestmentTransactionSummaryRequest)
    }

    public updateFundInvestmentRecentFinancialPerformance(
        fundId: number,
        fundReportId: number,
        investmentId: number,
        recentFinancialPerformance: string
    ): Observable<FundInvestment> {
        const updateFundInvestmentRecentFinancialPerformanceRequest: UpdateFundInvestmentRecentFinancialPerformanceRequest = {
            recentFinancialPerformance
        }
        return this.fundApiService.updateFundInvestmentRecentFinancialPerformance(fundId, fundReportId, investmentId, updateFundInvestmentRecentFinancialPerformanceRequest)
    }

    public updateFundInvestmentDevelopmentsSinceInvestment(
        fundId: number,
        fundReportId: number,
        investmentId: number,
        developmentsSinceInvestment: string
    ): Observable<FundInvestment> {
        const updateFundInvestmentDevelopmentsSinceInvestmentRequest: UpdateFundInvestmentDevelopmentsSinceInvestmentRequest = {
            developmentsSinceInvestment
        }
        return this.fundApiService.updateFundInvestmentDevelopmentsSinceInvestment(fundId, fundReportId, investmentId, updateFundInvestmentDevelopmentsSinceInvestmentRequest)
    }

    public calcFundInvestment(fundId: number, fundReportId: number, investmentId: number, investment: FundInvestment): Observable<FundInvestment> {
        return this.fundApiService.calculateFundInvestment(fundId, fundReportId, investmentId, investment);
    }

    public getPartnershipInvestment(fundId: number, fundReportId: number, fundValuationId?: number): Observable<PartnershipInvestment> {
        return this.fundApiService.getPartnershipInvestments(fundId, fundReportId, fundValuationId);
    }
}
